// Voice Command Center - Full CRUD with State Management
// Hands-free voice commands with NLP processing

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mic, MicOff, Volume2, Settings,
  History, Command, Check, X, ChevronRight,
  Plus, Trash2, Edit2, Save, Search, AudioLines,
  Bookmark, RefreshCw, Copy, BookOpen, Layers, Share2, Sparkles,
  Play, Globe, Compass, ExternalLink, ArrowRight, Library, SlidersHorizontal, Tag,
} from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../services/api-client';
import { PageLayout } from '@/components/layout/PageLayout';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal, ConfirmDialog } from '../components/ui/Modal';
import { useVoiceCommand } from '../hooks/useVoiceCommand';
import { useAuthStore } from '@/stores/authStore';
import { useTabMonitorSessionOptional } from '@/context/TabMonitorSessionContext';
import { executeTabVoiceAction, normalizeVoiceAction, parseVoiceTranscript, playVoiceFeedback } from '@/utils/voiceActions';

const CUSTOM_TEMPLATES_KEY = 'khiimori_voice_templates';

interface VoiceCommand {
  id: string;
  transcript: string;
  action: string;
  target?: string;
  confidence: number;
  executionResult?: {
    success: boolean;
    message: string;
  };
  timestamp: string;
  duration?: number;
}

export interface CommandTemplate {
  id: string;
  actionType: string;
  patterns: string[];
  description: string;
  icon?: string;
  enabled: boolean;
  usageCount: number;
  createdAt: string;
  targetUrl?: string;
  targetRoute?: string;
}

interface VoiceSettings {
  language: string;
  confidenceThreshold: number;
  autoExecute: boolean;
  soundFeedback: boolean;
  wakeWord: string;
}

function normalizeVoiceCommand(raw: Record<string, unknown>): VoiceCommand {
  const parsed = raw.parsed_command as Record<string, unknown> | undefined;
  const executionResult = (raw.execution_result ?? raw.executionResult) as VoiceCommand['executionResult'];
  return {
    id: String(raw.id ?? raw.command_id ?? ''),
    transcript: String(raw.transcript ?? ''),
    action: normalizeVoiceAction(String(raw.action ?? parsed?.action ?? 'unknown')),
    target: raw.target != null ? String(raw.target) : parsed?.target != null ? String(parsed.target) : undefined,
    confidence: Number(raw.confidence ?? parsed?.confidence ?? 0),
    executionResult,
    timestamp: String(raw.timestamp ?? raw.created_at ?? new Date().toISOString()),
    duration: raw.duration != null ? Number(raw.duration) : undefined,
  };
}

function loadCustomTemplates(): CommandTemplate[] {
  try {
    const raw = localStorage.getItem(CUSTOM_TEMPLATES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map((item) => normalizeCommandTemplate(item as Record<string, unknown>)) : [];
  } catch {
    return [];
  }
}

function saveCustomTemplates(templates: CommandTemplate[]) {
  localStorage.setItem(CUSTOM_TEMPLATES_KEY, JSON.stringify(templates));
}

function mergeParsedCommand(
  command: VoiceCommand,
  transcript: string,
  templates: CommandTemplate[],
): VoiceCommand {
  const local = parseVoiceTranscript(transcript, templates);
  const action = command.action !== 'unknown' ? command.action : local.action;
  return {
    ...command,
    action: normalizeVoiceAction(action),
    target: command.target ?? local.target,
    confidence: Math.max(command.confidence, local.confidence),
  };
}

function localParseCommand(transcriptText: string, templates: CommandTemplate[] = []): VoiceCommand {
  const parsed = parseVoiceTranscript(transcriptText, templates);
  return {
    id: `local-${Date.now()}`,
    transcript: transcriptText,
    action: parsed.action,
    target: parsed.target,
    confidence: parsed.confidence,
    timestamp: new Date().toISOString(),
  };
}

function normalizeCommandTemplate(raw: Record<string, unknown>): CommandTemplate {
  return {
    id: String(raw.id ?? raw.template_id ?? ''),
    actionType: String(raw.action_type ?? raw.actionType ?? 'unknown'),
    patterns: Array.isArray(raw.patterns) ? raw.patterns.map(String) : [],
    description: String(raw.description ?? ''),
    icon: raw.icon != null ? String(raw.icon) : undefined,
    enabled: raw.enabled !== false,
    usageCount: Number(raw.usage_count ?? raw.usageCount ?? 0),
    createdAt: String(raw.created_at ?? raw.createdAt ?? ''),
    targetUrl: raw.target_url != null ? String(raw.target_url) : raw.targetUrl != null ? String(raw.targetUrl) : undefined,
    targetRoute: raw.target_route != null ? String(raw.target_route) : raw.targetRoute != null ? String(raw.targetRoute) : undefined,
  };
}

export interface TemplatePreset {
  id: string;
  name: string;
  category: 'website' | 'search' | 'nav' | 'ai' | 'tabs';
  actionType: string;
  patterns: string[];
  description: string;
  targetUrl?: string;
  targetRoute?: string;
}

export const TEMPLATE_PRESETS: TemplatePreset[] = [
  {
    id: 'preset-youtube',
    name: 'YouTube',
    category: 'website',
    actionType: 'open_youtube',
    patterns: ['open youtube', 'go to youtube', 'watch youtube'],
    description: 'Open YouTube video portal in browser',
    targetUrl: 'https://www.youtube.com',
  },
  {
    id: 'preset-github',
    name: 'GitHub',
    category: 'website',
    actionType: 'open_github',
    patterns: ['open github', 'go to github', 'show github'],
    description: 'Open GitHub repositories and dashboard',
    targetUrl: 'https://github.com',
  },
  {
    id: 'preset-gmail',
    name: 'Gmail',
    category: 'website',
    actionType: 'open_gmail',
    patterns: ['open mail', 'open gmail', 'check email'],
    description: 'Open Google Mail inbox in your browser',
    targetUrl: 'https://mail.google.com',
  },
  {
    id: 'preset-chatgpt',
    name: 'ChatGPT',
    category: 'ai',
    actionType: 'open_chatgpt',
    patterns: ['open chatgpt', 'chat with ai', 'launch chatgpt'],
    description: 'Launch ChatGPT assistant in browser',
    targetUrl: 'https://chat.openai.com',
  },
  {
    id: 'preset-google-search',
    name: 'Google Search',
    category: 'search',
    actionType: 'search_google',
    patterns: ['google {query}', 'search google for {query}'],
    description: 'Search Google directly with your voice query',
    targetUrl: 'https://www.google.com/search?q={query}',
  },
  {
    id: 'preset-youtube-search',
    name: 'YouTube Search',
    category: 'search',
    actionType: 'search_youtube',
    patterns: ['search youtube for {query}', 'youtube {query}'],
    description: 'Find videos on YouTube for any topic',
    targetUrl: 'https://www.youtube.com/results?search_query={query}',
  },
  {
    id: 'preset-reddit-search',
    name: 'Reddit Search',
    category: 'search',
    actionType: 'search_reddit',
    patterns: ['search reddit for {query}', 'reddit {query}'],
    description: 'Search discussions and posts on Reddit',
    targetUrl: 'https://www.reddit.com/search/?q={query}',
  },
  {
    id: 'preset-spotify',
    name: 'Spotify',
    category: 'website',
    actionType: 'open_spotify',
    patterns: ['open spotify', 'play music', 'launch spotify'],
    description: 'Open Spotify web player in your browser',
    targetUrl: 'https://open.spotify.com',
  },
  {
    id: 'preset-notion',
    name: 'Notion',
    category: 'website',
    actionType: 'open_notion',
    patterns: ['open notion', 'go to notion', 'open notes'],
    description: 'Open Notion workspace and notes',
    targetUrl: 'https://www.notion.so',
  },
  {
    id: 'preset-nav-productivity',
    name: 'Productivity Center',
    category: 'nav',
    actionType: 'nav_productivity',
    patterns: ['open productivity', 'focus session', 'go to focus'],
    description: 'Navigate to Khiimori Focus & Productivity center',
    targetRoute: '/productivity',
  },
  {
    id: 'preset-nav-ai',
    name: 'AI Studio',
    category: 'ai',
    actionType: 'nav_ai',
    patterns: ['open ai studio', 'go to ai', 'open assistant'],
    description: 'Navigate to Khiimori Conversational AI Studio',
    targetRoute: '/ai',
  },
  {
    id: 'preset-nav-insights',
    name: 'Insights & Stats',
    category: 'nav',
    actionType: 'nav_insights',
    patterns: ['open insights', 'show analytics', 'open stats'],
    description: 'Navigate to Khiimori Analytics and Insights',
    targetRoute: '/insights',
  },
  {
    id: 'preset-nav-home',
    name: 'Home Overview',
    category: 'nav',
    actionType: 'nav_home',
    patterns: ['go home', 'open home', 'back to dashboard'],
    description: 'Return to the Khiimori Home overview',
    targetRoute: '/home',
  },
  {
    id: 'preset-ask-ai',
    name: 'Ask AI Query',
    category: 'ai',
    actionType: 'ask_ai',
    patterns: ['ask ai {query}', 'ask assistant {query}'],
    description: 'Submit an instant question to Khiimori AI Assistant',
    targetRoute: '/ai',
  },
];

const actionIcons: Record<string, React.ReactNode> = {
  close: <X className="w-5 h-5 text-error-400" />,
  close_tab: <X className="w-5 h-5 text-error-400" />,
  pin: <Command className="w-5 h-5 text-primary-400" />,
  pin_tab: <Command className="w-5 h-5 text-primary-400" />,
  switch: <ChevronRight className="w-5 h-5 text-info-400" />,
  switch_tab: <ChevronRight className="w-5 h-5 text-info-400" />,
  mute: <Volume2 className="w-5 h-5 text-warning-400" />,
  mute_tabs: <Volume2 className="w-5 h-5 text-warning-400" />,
  stash: <Save className="w-5 h-5 text-success-400" />,
  stash_tab: <Save className="w-5 h-5 text-success-400" />,
  search: <Search className="w-5 h-5 text-primary-400" />,
  search_tabs: <Search className="w-5 h-5 text-primary-400" />,
  new_tab: <Plus className="w-5 h-5 text-success-400" />,
  group: <Layers className="w-5 h-5 text-primary-400" />,
  group_tabs: <Layers className="w-5 h-5 text-primary-400" />,
  bookmark_tab: <Bookmark className="w-5 h-5 text-amber-400" />,
  duplicate_tab: <Copy className="w-5 h-5 text-cyan-400" />,
  reload_tab: <RefreshCw className="w-5 h-5 text-emerald-400" />,
  focus_mode: <Command className="w-5 h-5 text-violet-400" />,
  synthesize_research: <BookOpen className="w-5 h-5 text-indigo-400" />,
  open_arxiv: <BookOpen className="w-5 h-5 text-rose-400" />,
  dedup_tabs: <Sparkles className="w-5 h-5 text-teal-400" />,
  share_tab: <Share2 className="w-5 h-5 text-sky-400" />,
  extract_content: <BookOpen className="w-5 h-5 text-amber-300" />,
  export_session: <Save className="w-5 h-5 text-emerald-300" />,
  open_youtube: <Globe className="w-5 h-5 text-red-400" />,
  open_github: <Command className="w-5 h-5 text-gray-300" />,
  open_gmail: <Bookmark className="w-5 h-5 text-red-400" />,
  open_chatgpt: <Sparkles className="w-5 h-5 text-emerald-400" />,
  open_spotify: <Volume2 className="w-5 h-5 text-green-400" />,
  open_notion: <BookOpen className="w-5 h-5 text-gray-200" />,
  search_google: <Search className="w-5 h-5 text-blue-400" />,
  search_youtube: <Search className="w-5 h-5 text-red-400" />,
  search_reddit: <Search className="w-5 h-5 text-orange-400" />,
  nav_productivity: <Command className="w-5 h-5 text-violet-400" />,
  nav_ai: <Sparkles className="w-5 h-5 text-indigo-400" />,
  nav_insights: <Layers className="w-5 h-5 text-sky-400" />,
  nav_home: <Plus className="w-5 h-5 text-emerald-400" />,
  ask_ai: <Sparkles className="w-5 h-5 text-amber-400" />,
};

function getActionIcon(actionType: string): React.ReactNode {
  if (actionIcons[actionType]) return actionIcons[actionType];
  if (actionType.startsWith('open_')) return <Globe className="w-5 h-5 text-sky-400" />;
  if (actionType.startsWith('search_')) return <Search className="w-5 h-5 text-amber-400" />;
  if (actionType.startsWith('nav_') || actionType.startsWith('go_')) return <ChevronRight className="w-5 h-5 text-indigo-400" />;
  if (actionType.startsWith('ai_') || actionType.includes('ai')) return <Sparkles className="w-5 h-5 text-violet-400" />;
  return <Command className="w-5 h-5 text-khiimori-text-muted" />;
}

export default function VoiceCommandCenterPage() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const { workspaceSessionId } = useTabMonitorSessionOptional() ?? { workspaceSessionId: null };

  // State management
  const [commands, setCommands] = useState<VoiceCommand[]>([]);
  const [templates, setTemplates] = useState<CommandTemplate[]>([]);
  const [customTemplates, setCustomTemplates] = useState<CommandTemplate[]>(() => loadCustomTemplates());
  const [transcript, setTranscript] = useState('');
  const [typedCommand, setTypedCommand] = useState('');
  const [currentCommand, setCurrentCommand] = useState<VoiceCommand | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [showHistory, setShowHistory] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [activeTab, setActiveTab] = useState<'live' | 'history' | 'templates'>('live');
  const [searchQuery, setSearchQuery] = useState('');
  const [templateFilter, setTemplateFilter] = useState<'all' | 'builtin' | 'custom'>('all');
  const [templateCategory, setTemplateCategory] = useState<'website' | 'search' | 'nav' | 'tabs' | 'ai' | 'custom'>('website');

  // Modal states
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(false);
  const [showEditTemplateModal, setShowEditTemplateModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<CommandTemplate | null>(null);

  // Settings state
  const [settings, setSettings] = useState<VoiceSettings>({
    language: 'en-US',
    confidenceThreshold: 0.7,
    autoExecute: false,
    soundFeedback: true,
    wakeWord: 'Hey Khiimori',
  });

  // Form state
  const [templateFormData, setTemplateFormData] = useState({
    actionType: '',
    description: '',
    patterns: [] as string[],
    targetUrl: '',
    targetRoute: '',
  });
  const [patternInput, setPatternInput] = useState('');

  const allTemplates = useMemo(
    () => [...templates, ...customTemplates],
    [templates, customTemplates],
  );

  const runCommand = useCallback(async (command: VoiceCommand) => {
    if (!command.id) return;
    try {
      if (!command.id.startsWith('local-')) {
        try {
          await apiClient.confirmVoiceCommand(command.id);
        } catch {
          // Confirm is bookkeeping only — never block the real tab action.
        }
      }
      const result = await executeTabVoiceAction(
        command.action,
        command.transcript,
        command.target,
        workspaceSessionId,
        navigate,
        allTemplates,
      );
      const executed: VoiceCommand = {
        ...command,
        executionResult: result,
      };
      setCommands((prev) => [executed, ...prev.filter((c) => c.id !== command.id)]);
      setCurrentCommand(executed);
      playVoiceFeedback(result.message, settings.soundFeedback);
      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
      setTimeout(() => {
        setCurrentCommand(null);
        setTranscript('');
      }, 3000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to execute command';
      setError(msg);
      toast.error(msg);
    }
  }, [allTemplates, navigate, settings.soundFeedback, workspaceSessionId]);

  const processTranscript = useCallback(async (transcriptText: string) => {
    const trimmed = transcriptText.trim();
    if (!trimmed) return;

    const templateCtx = [...templates, ...customTemplates];

    try {
      setError(null);
      const raw = await apiClient.submitVoiceTranscript({
        transcript: trimmed,
        session_id: workspaceSessionId ?? undefined,
      });
      const command = mergeParsedCommand(
        normalizeVoiceCommand(raw as Record<string, unknown>),
        trimmed,
        templateCtx,
      );
      setCurrentCommand(command);
      if (command.action === 'unknown') {
        toast.info('Phrase heard, but no action matched — try a quick command below or check Templates.');
        return;
      }
      if (settings.autoExecute && command.confidence >= settings.confidenceThreshold) {
        await runCommand(command);
      }
    } catch (err) {
      const command = localParseCommand(trimmed, templateCtx);
      setCurrentCommand(command);
      if (command.action === 'unknown') {
        const msg = err instanceof Error ? err.message : 'Could not process command';
        setError(msg);
        toast.error(msg);
        return;
      }
      toast.warning(err instanceof Error ? err.message : 'Server unavailable — parsed command locally');
      if (settings.autoExecute && command.confidence >= settings.confidenceThreshold) {
        await runCommand(command);
      }
    }
  }, [customTemplates, runCommand, settings.autoExecute, settings.confidenceThreshold, templates, workspaceSessionId]);

  const {
    isListening,
    isProcessing,
    isSupported,
    error: voiceError,
    startListening,
    stopListening,
  } = useVoiceCommand({
    language: settings.language,
    onTranscript: (text) => {
      setTranscript(text);
      setTypedCommand(text);
      return processTranscript(text);
    },
    onError: (message) => setError(message),
  });

  // Fetch data on mount
  useEffect(() => {
    void fetchCommands();
    void fetchTemplates();
    void loadVoiceSettings();
  }, [user?.id]);

  const loadVoiceSettings = async () => {
    if (!user?.id) return;
    try {
      const profile = await apiClient.getVoiceProfile(user.id);
      if (profile && typeof profile === 'object') {
        setSettings((prev) => ({
          ...prev,
          language: String(profile.language ?? prev.language),
          wakeWord: String(profile.wake_word ?? profile.wakeWord ?? prev.wakeWord),
          confidenceThreshold: Number(profile.confidence_threshold ?? profile.confidenceThreshold ?? prev.confidenceThreshold),
          autoExecute: Boolean(profile.auto_execute ?? profile.autoExecute ?? prev.autoExecute),
          soundFeedback: Boolean(profile.sound_feedback ?? profile.soundFeedback ?? prev.soundFeedback),
        }));
      }
    } catch {
      // Profile is optional; keep local defaults.
    }
  };

  const fetchCommands = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await apiClient.getVoiceCommands();
      setCommands(data.map((item) => normalizeVoiceCommand(item)));
    } catch (err: any) {
      setError(err.message || 'Failed to fetch commands');
      setCommands([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const data = await apiClient.getVoiceCommandTemplates();
      setTemplates(data.map((item) => normalizeCommandTemplate(item)));
    } catch (err) {
      console.error('Error fetching templates:', err);
      setTemplates([]);
    }
  };

  const builtinTemplateIds = useMemo(() => new Set(templates.map((t) => t.id)), [templates]);

  // CRUD Operations — custom templates are stored locally
  const createTemplate = async () => {
    try {
      const newTemplate: CommandTemplate = {
        id: `custom-${Date.now()}`,
        actionType: templateFormData.actionType.toLowerCase().replace(/\s+/g, '_'),
        patterns: templateFormData.patterns,
        description: templateFormData.description,
        targetUrl: templateFormData.targetUrl.trim() || undefined,
        targetRoute: templateFormData.targetRoute.trim() || undefined,
        enabled: true,
        usageCount: 0,
        createdAt: new Date().toISOString(),
      };

      const next = [...customTemplates, newTemplate];
      setCustomTemplates(next);
      saveCustomTemplates(next);
      setShowCreateTemplateModal(false);
      resetTemplateForm();
      toast.success('Template saved');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to create template';
      setError(msg);
    }
  };

  const updateTemplate = async (id: string) => {
    try {
      const isBuiltin = builtinTemplateIds.has(id);
      const updated: CommandTemplate = {
        ...(customTemplates.find((t) => t.id === id) ?? selectedTemplate!),
        id: isBuiltin ? `custom-${Date.now()}` : id,
        actionType: templateFormData.actionType.toLowerCase().replace(/\s+/g, '_'),
        patterns: templateFormData.patterns,
        description: templateFormData.description,
        targetUrl: templateFormData.targetUrl.trim() || undefined,
        targetRoute: templateFormData.targetRoute.trim() || undefined,
      };

      let next: CommandTemplate[];
      if (isBuiltin) {
        next = [...customTemplates, updated];
      } else {
        next = customTemplates.map((t) => (t.id === id ? updated : t));
      }
      setCustomTemplates(next);
      saveCustomTemplates(next);
      setShowEditTemplateModal(false);
      setSelectedTemplate(null);
      resetTemplateForm();
      toast.success(isBuiltin ? 'Customized template saved' : 'Template updated');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to update template';
      setError(msg);
    }
  };

  const deleteTemplate = async (id: string) => {
    try {
      if (builtinTemplateIds.has(id)) {
        toast.error('Built-in templates cannot be deleted');
        return;
      }
      const next = customTemplates.filter((t) => t.id !== id);
      setCustomTemplates(next);
      saveCustomTemplates(next);
      setShowDeleteConfirm(null);
      if (selectedTemplate?.id === id) {
        setSelectedTemplate(null);
      }
      toast.success('Template deleted');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to delete template';
      setError(msg);
    }
  };

  const toggleTemplateEnabled = (id: string) => {
    if (builtinTemplateIds.has(id)) {
      setTemplates((prev) => prev.map((t) => (t.id === id ? { ...t, enabled: !t.enabled } : t)));
      return;
    }
    const next = customTemplates.map((t) => (t.id === id ? { ...t, enabled: !t.enabled } : t));
    setCustomTemplates(next);
    saveCustomTemplates(next);
  };

  const resetTemplateForm = () => {
    setTemplateFormData({
      actionType: '',
      description: '',
      patterns: [],
      targetUrl: '',
      targetRoute: '',
    });
    setPatternInput('');
    setTemplateCategory('website');
  };

  const handleAddPattern = () => {
    if (patternInput.trim() && !templateFormData.patterns.includes(patternInput.trim())) {
      setTemplateFormData(prev => ({
        ...prev,
        patterns: [...prev.patterns, patternInput.trim()],
      }));
      setPatternInput('');
    }
  };

  const handleRemovePattern = (pattern: string) => {
    setTemplateFormData(prev => ({
      ...prev,
      patterns: prev.patterns.filter(p => p !== pattern),
    }));
  };

  const handleAddPreset = (preset: TemplatePreset) => {
    const exists = allTemplates.some(
      (t) => t.actionType.toLowerCase() === preset.actionType.toLowerCase(),
    );
    if (exists) {
      toast.info(`"${preset.name}" is already in your templates`);
      return;
    }
    const newTemplate: CommandTemplate = {
      id: `custom-${Date.now()}`,
      actionType: preset.actionType,
      patterns: preset.patterns,
      description: preset.description,
      targetUrl: preset.targetUrl,
      targetRoute: preset.targetRoute,
      enabled: true,
      usageCount: 0,
      createdAt: new Date().toISOString(),
    };
    const next = [...customTemplates, newTemplate];
    setCustomTemplates(next);
    saveCustomTemplates(next);
    toast.success(`Added "${preset.name}" template`);
  };

  const handleTestTemplate = async (template: CommandTemplate) => {
    const samplePattern = (template.patterns && template.patterns[0]) || template.actionType;
    const testTranscript = samplePattern
      .replace(/\{tab\}/gi, 'current tab')
      .replace(/\{query\}/gi, 'Khiimori')
      .replace(/\{target\}/gi, 'Khiimori')
      .replace(/\{topic\}/gi, 'research')
      .replace(/\{url\}/gi, 'https://github.com');

    const cmd: VoiceCommand = {
      id: `local-test-${Date.now()}`,
      transcript: testTranscript,
      action: template.actionType,
      target: samplePattern.includes('{query}') ? 'Khiimori' : undefined,
      confidence: 1.0,
      timestamp: new Date().toISOString(),
    };

    if (builtinTemplateIds.has(template.id)) {
      setTemplates((prev) =>
        prev.map((t) => (t.id === template.id ? { ...t, usageCount: (t.usageCount || 0) + 1 } : t)),
      );
    } else {
      const next = customTemplates.map((t) =>
        t.id === template.id ? { ...t, usageCount: (t.usageCount || 0) + 1 } : t,
      );
      setCustomTemplates(next);
      saveCustomTemplates(next);
    }

    await runCommand(cmd);
  };

  const openEditTemplateModal = (template: CommandTemplate) => {
    const isBuiltin = builtinTemplateIds.has(template.id);
    setSelectedTemplate(template);
    setTemplateFormData({
      actionType: isBuiltin ? `${template.actionType}_custom` : template.actionType,
      description: template.description,
      patterns: [...template.patterns],
      targetUrl: template.targetUrl || '',
      targetRoute: template.targetRoute || '',
    });
    if (isBuiltin) {
      toast.info('Cloning as a custom template for you to edit.');
    }
    setShowEditTemplateModal(true);
  };

  const handleToggleListening = () => {
    if (isListening) {
      stopListening();
      return;
    }
    setTranscript('');
    setCurrentCommand(null);
    setError(null);
    void startListening();
  };

  const handleSubmitTypedCommand = () => {
    const text = typedCommand.trim();
    if (!text) return;
    setTranscript(text);
    void processTranscript(text);
  };

  const handleConfirmCommand = () => {
    if (!currentCommand) return;
    if (currentCommand.action === 'unknown') {
      toast.info('Try "Close the GitHub tab", "Search for React", or "Open new tab".');
      return;
    }
    void runCommand(currentCommand);
  };

  const handleDismissCommand = () => {
    setCurrentCommand(null);
    setTranscript('');
  };

  const clearHistory = async () => {
    try {
      await Promise.all(commands.map((cmd) => apiClient.deleteVoiceCommand(cmd.id)));
      setCommands([]);
      toast.success('History cleared');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to clear history';
      toast.error(msg);
    }
  };

  const runQuickCommand = (phrase: string) => {
    setTranscript(phrase);
    setTypedCommand(phrase);
    void processTranscript(phrase);
  };

  // Filter commands
  const filteredCommands = commands.filter(cmd => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        cmd.transcript.toLowerCase().includes(query) ||
        cmd.action.toLowerCase().includes(query) ||
        (cmd.target && cmd.target.toLowerCase().includes(query))
      );
    }
    return true;
  });

  // Filter templates
  const filteredTemplates = useMemo(() => {
    let list = allTemplates;
    if (templateFilter === 'builtin') {
      list = list.filter((t) => builtinTemplateIds.has(t.id));
    } else if (templateFilter === 'custom') {
      list = list.filter((t) => !builtinTemplateIds.has(t.id));
    }
    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase().trim();
    return list.filter(
      (t) =>
        t.actionType.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        t.patterns.some((p) => p.toLowerCase().includes(q)) ||
        (t.targetUrl && t.targetUrl.toLowerCase().includes(q))
    );
  }, [allTemplates, builtinTemplateIds, searchQuery, templateFilter]);

  const getActionLabel = (action: string) => {
    return action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  return (
    <PageLayout
      title="Voice Command Center"
      description="Hands-free voice commands with NLP processing"
      icon={Mic}
    >
      {/* Header */}
      <div className="bg-khiimori-bg-elevated/5 backdrop-blur-xl border-b border-khiimori-border/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Button
                onClick={() => setShowTemplates(!showTemplates)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all focus-visible:ring-2 focus-visible:ring-primary-500/50 ${
                  showTemplates ? 'bg-primary-500 text-white' : 'bg-khiimori-bg-elevated/5 text-khiimori-text-secondary hover:bg-khiimori-bg-elevated/10'
                }`}
              >
                <Command className="w-4 h-4" />
                Templates
              </Button>
              <Button
                onClick={() => setShowSettings(!showSettings)}
                aria-label="Toggle settings"
                className="p-2 bg-khiimori-bg-elevated/5 hover:bg-khiimori-bg-elevated/10 rounded-lg transition-all focus-visible:ring-2 focus-visible:ring-primary-500/50"
              >
                <Settings className="w-5 h-5 text-khiimori-text-secondary" />
              </Button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex items-center gap-4">
            {[
              { id: 'live', label: 'Live', icon: AudioLines },
              { id: 'history', label: 'History', icon: History },
              { id: 'templates', label: 'Templates', icon: Command },
            ].map(tab => (
              <Button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all focus-visible:ring-2 focus-visible:ring-primary-500/50 ${
                  activeTab === tab.id
                    ? 'bg-primary-500 text-white'
                    : 'bg-khiimori-bg-elevated/5 text-khiimori-text-muted hover:text-white hover:bg-khiimori-bg-elevated/5'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.id === 'history' && commands.length > 0 && (
                  <span className="ml-1 text-xs opacity-70">({commands.length})</span>
                )}
              </Button>
            ))}

            {/* Search */}
            <div className="ml-auto relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-khiimori-text-muted" />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search commands..."
                className="pl-10 pr-4 py-2 bg-khiimori-bg-elevated/5 border border-khiimori-border/10 rounded-lg text-khiimori-text-primary placeholder-khiimori-text-muted focus:outline-none focus:border-primary-500/50 w-64"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {(voiceError || error) && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-error-500/20 border border-error-500/50 rounded-xl text-error-400 flex items-center justify-between"
          >
            <span>{error || voiceError}</span>
            <Button onClick={() => setError(null)} aria-label="Dismiss error" className="p-1 hover:bg-error-500/30 rounded focus-visible:ring-2 focus-visible:ring-error-500/50">
              <X className="w-4 h-4" />
            </Button>
          </motion.div>
        )}

        {!isSupported && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-warning-500/20 border border-warning-500/50 rounded-xl text-warning-200"
          >
            Voice recognition is not available in this browser. Use Chrome or Edge, or type your command below.
          </motion.div>
        )}

        {activeTab === 'live' && (
          <div className="grid grid-cols-3 gap-6">
            {/* Main Voice Interface */}
            <div className="col-span-2 space-y-6">
              {/* Voice Input Area */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-khiimori-bg-elevated/5 backdrop-blur-xl rounded-3xl p-8 border border-khiimori-border/10 text-center"
              >
                {/* Audio Waveform Visualization */}
                <div className="relative w-48 h-48 mx-auto mb-8">
                  {/* Outer ring */}
                  <motion.div
                    animate={isListening ? {
                      scale: [1, 1.1, 1],
                      opacity: [0.5, 0.8, 0.5]
                    } : {}}
                    transition={{ duration: 1, repeat: Infinity }}
                    className="absolute inset-0 rounded-full border-4 border-primary-500/30"
                  />

                  {/* Middle ring */}
                  <motion.div
                    animate={isListening ? {
                      scale: [1, 1.15, 1],
                      opacity: [0.4, 0.7, 0.4]
                    } : {}}
                    transition={{ duration: 0.8, repeat: Infinity }}
                    className="absolute inset-4 rounded-full border-4 border-primary-500/40"
                  />

                  {/* Inner circle - Microphone button */}
                  <motion.button
                    onClick={handleToggleListening}
                    disabled={isProcessing}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label={isListening ? 'Stop listening' : 'Start listening'}
                    className={`absolute inset-8 rounded-full flex items-center justify-center transition-all ${
                      isListening
                        ? 'bg-gradient-to-br from-primary-500 to-info-500 shadow-lg shadow-primary-500/40'
                        : 'bg-gradient-to-br from-khiimori-bg-tertiary to-khiimori-bg-secondary border-4 border-khiimori-border hover:border-primary-500'
                    }`}
                  >
                    {isListening ? (
                      <MicOff className="w-12 h-12 text-khiimori-text-primary" />
                    ) : (
                      <Mic className="w-12 h-12 text-khiimori-text-secondary" />
                    )}
                  </motion.button>

                  {/* Audio level indicators */}
                  {isListening && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      {[...Array(8)].map((_, i) => (
                        <motion.div
                          key={i}
                          className="absolute w-1 bg-primary-400 rounded-full"
                          style={{
                            height: `${20 + Math.random() * 30}px`,
                            transform: `rotate(${i * 45}deg) translateY(-60px)`,
                            transformOrigin: 'center'
                          }}
                          animate={{
                            height: [20 + Math.random() * 30, 40 + Math.random() * 40, 20 + Math.random() * 30]
                          }}
                          transition={{
                            duration: 0.5,
                            repeat: Infinity,
                            delay: i * 0.1
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Status Text */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mb-6"
                >
                  {isListening ? (
                    <p className="text-primary-400 text-lg font-medium">Listening...</p>
                  ) : isProcessing ? (
                    <p className="text-primary-400 text-lg font-medium">Processing command...</p>
                  ) : !isSupported ? (
                    <p className="text-khiimori-text-muted">Type a command below</p>
                  ) : (
                    <p className="text-khiimori-text-muted">Click the microphone and speak clearly</p>
                  )}
                </motion.div>

                {/* Typed command fallback — works when mic is blocked or unsupported */}
                <div className="mb-6 max-w-xl mx-auto">
                  <label className="block text-left text-khiimori-text-secondary text-sm mb-2">
                    Type or edit command
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      value={typedCommand}
                      onChange={(e) => setTypedCommand(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSubmitTypedCommand();
                        }
                      }}
                      placeholder='e.g. "Close the GitHub tab" or "Search for React"'
                      className="flex-1 px-4 py-3 bg-khiimori-bg-elevated/5 border border-khiimori-border/10 rounded-xl text-khiimori-text-primary placeholder-khiimori-text-muted"
                    />
                    <Button
                      variant="primary"
                      onClick={handleSubmitTypedCommand}
                      disabled={!typedCommand.trim() || isProcessing}
                      className="px-5 py-3 rounded-xl"
                    >
                      Send
                    </Button>
                  </div>
                </div>

                {/* Transcript Display */}
                {transcript && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-khiimori-bg-elevated/5 rounded-2xl p-6 mb-6"
                  >
                    <p className="text-khiimori-text-primary text-lg mb-2">"{transcript}"</p>
                    {currentCommand && !currentCommand.executionResult && (
                      <p className="text-sm text-primary-300 mb-4">
                        Detected: {getActionLabel(currentCommand.action)}
                        {currentCommand.target ? ` → ${currentCommand.target}` : ''}
                        {' '}({Math.round(currentCommand.confidence * 100)}% confidence)
                      </p>
                    )}
                    <div className="flex items-center justify-center gap-4">
                      <Button
                        onClick={handleConfirmCommand}
                        disabled={!currentCommand || currentCommand.action === 'unknown'}
                        className="flex items-center gap-2 px-6 py-3 bg-success-500 hover:bg-success-600 text-khiimori-text-primary rounded-xl transition-all focus-visible:ring-2 focus-visible:ring-success-500/50 disabled:opacity-50"
                      >
                        <Check className="w-5 h-5" />
                        Execute
                      </Button>
                      <Button
                        onClick={handleDismissCommand}
                        className="flex items-center gap-2 px-6 py-3 bg-khiimori-bg-elevated/10 hover:bg-khiimori-bg-elevated/15 text-khiimori-text-primary rounded-xl transition-all focus-visible:ring-2 focus-visible:ring-primary-500/50"
                      >
                        <X className="w-5 h-5" />
                        Cancel
                      </Button>
                    </div>
                  </motion.div>
                )}

                {/* Quick Commands */}
                <div className="space-y-2">
                  <div className="text-xs font-semibold uppercase tracking-wider text-khiimori-text-muted">
                    Quick Commands & Templates
                  </div>
                  <div className="flex items-center justify-center gap-2 flex-wrap">
                    {[
                      'Close the GitHub tab',
                      'Switch to documentation',
                      'Search for React',
                      'Open new tab',
                      'Organize tabs into groups',
                      'Bookmark this tab',
                      'Synthesize research',
                      'Open arXiv',
                      'Close duplicate tabs',
                      'Enter focus mode',
                      'Stash inactive tabs',
                    ].map(cmd => (
                      <Button
                        key={cmd}
                        onClick={() => runQuickCommand(cmd)}
                        className="px-3.5 py-1.5 bg-khiimori-bg-elevated/5 hover:bg-khiimori-bg-elevated/15 text-khiimori-text-secondary hover:text-khiimori-text-primary rounded-full text-xs transition-all focus-visible:ring-2 focus-visible:ring-primary-500/50"
                      >
                        "{cmd}"
                      </Button>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Current Command Result */}
              <AnimatePresence>
                {currentCommand?.executionResult && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="bg-khiimori-bg-elevated/5 backdrop-blur-xl rounded-2xl p-6 border border-khiimori-border/10"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-xl ${currentCommand.executionResult?.success ? 'bg-success-500/20' : 'bg-error-500/20'}`}>
                          {currentCommand.executionResult?.success ? (
                            <Check className="w-6 h-6 text-success-400" />
                          ) : (
                            <X className="w-6 h-6 text-error-400" />
                          )}
                        </div>
                        <div>
                          <h3 className="text-khiimori-text-primary font-semibold">Command Executed</h3>
                          <p className="text-khiimori-text-muted">{currentCommand.executionResult?.message}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-primary-400 font-medium">{Math.round(currentCommand.confidence * 100)}% confidence</div>
                        <div className="text-khiimori-text-muted text-sm">
                          {new Date(currentCommand.timestamp).toLocaleTimeString()}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Voice Status */}
              <div className="bg-gradient-to-br from-primary-500/10 to-info-500/10 rounded-2xl p-6 border border-primary-500/30">
                <div className="flex items-center gap-3 mb-4">
                  <Mic className="w-6 h-6 text-primary-400" />
                  <h3 className="text-khiimori-text-primary font-semibold">Voice Status</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-khiimori-text-secondary">Status</span>
                    <span className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${isListening ? 'bg-primary-400 animate-pulse' : 'bg-khiimori-bg-tertiary'}`}></span>
                      <span className="text-primary-400 text-sm">{isListening ? 'Listening' : 'Ready'}</span>
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-khiimori-text-secondary">Language</span>
                    <span className="text-khiimori-text-primary text-sm">{settings.language}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-khiimori-text-secondary">Confidence</span>
                    <span className="text-khiimori-text-primary text-sm">{Math.round(settings.confidenceThreshold * 100)}%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-khiimori-text-secondary">Workspace</span>
                    <span className={`text-sm ${workspaceSessionId ? 'text-success-400' : 'text-warning-400'}`}>
                      {workspaceSessionId ? 'Ready' : 'Open Live tab first'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-khiimori-text-secondary">Commands today</span>
                    <span className="text-khiimori-text-primary text-sm">{commands.length}</span>
                  </div>
                </div>
              </div>

              {/* Settings Panel */}
              {showSettings && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-khiimori-bg-elevated/5 backdrop-blur-xl rounded-2xl p-6 border border-khiimori-border/10"
                >
                  <h3 className="text-khiimori-text-primary font-semibold mb-4">Voice Settings</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-khiimori-text-secondary text-sm mb-2 block">Language</label>
                      <select
                        value={settings.language}
                        onChange={(e) => setSettings(prev => ({ ...prev, language: e.target.value }))}
                        className="w-full bg-khiimori-bg-elevated/5 border border-khiimori-border/10 rounded-lg px-3 py-2 text-khiimori-text-primary"
                      >
                        <option value="en-US">English (US)</option>
                        <option value="en-GB">English (UK)</option>
                        <option value="es-ES">Spanish</option>
                        <option value="fr-FR">French</option>
                        <option value="de-DE">German</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-khiimori-text-secondary text-sm mb-2 block">Confidence Threshold</label>
                      <Input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={settings.confidenceThreshold}
                        onChange={(e) => setSettings(prev => ({ ...prev, confidenceThreshold: parseFloat(e.target.value) }))}
                        className="w-full"
                      />
                      <div className="flex justify-between text-khiimori-text-muted text-xs mt-1">
                        <span>Low</span>
                        <span>{Math.round(settings.confidenceThreshold * 100)}%</span>
                        <span>High</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-khiimori-text-secondary text-sm">Auto-execute</span>
                      <Button
                        onClick={() => setSettings(prev => ({ ...prev, autoExecute: !prev.autoExecute }))}
                        aria-label={settings.autoExecute ? 'Disable auto-execute' : 'Enable auto-execute'}
                        className={`w-12 h-6 rounded-full transition-all focus-visible:ring-2 focus-visible:ring-primary-500/50 ${settings.autoExecute ? 'bg-primary-500' : 'bg-khiimori-bg-elevated/10'}`}
                      >
                        <div className={`w-5 h-5 bg-white rounded-full transition-all ${settings.autoExecute ? 'translate-x-6' : 'translate-x-0.5'}`} />
                      </Button>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-khiimori-text-secondary text-sm">Sound feedback</span>
                      <Button
                        onClick={() => setSettings(prev => ({ ...prev, soundFeedback: !prev.soundFeedback }))}
                        aria-label={settings.soundFeedback ? 'Disable sound feedback' : 'Enable sound feedback'}
                        className={`w-12 h-6 rounded-full transition-all focus-visible:ring-2 focus-visible:ring-primary-500/50 ${settings.soundFeedback ? 'bg-primary-500' : 'bg-khiimori-bg-elevated/10'}`}
                      >
                        <div className={`w-5 h-5 bg-white rounded-full transition-all ${settings.soundFeedback ? 'translate-x-6' : 'translate-x-0.5'}`} />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Tips */}
              <div className="bg-khiimori-bg-elevated/5 backdrop-blur-xl rounded-2xl p-6 border border-khiimori-border/10">
                <h3 className="text-khiimori-text-primary font-semibold mb-4">Voice Tips</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-primary-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-primary-400 text-xs font-bold">1</span>
                    </div>
                    <p className="text-khiimori-text-muted text-sm">Speak clearly and at a normal pace for best results</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-primary-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-primary-400 text-xs font-bold">2</span>
                    </div>
                    <p className="text-khiimori-text-muted text-sm">Use specific names like "GitHub tab" instead of "that tab"</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-primary-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-primary-400 text-xs font-bold">3</span>
                    </div>
                    <p className="text-khiimori-text-muted text-sm">Add "and close" to perform multiple actions at once</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-khiimori-text-primary">Command History</h2>
              <Button
                onClick={() => void clearHistory()}
                disabled={commands.length === 0}
                className="px-4 py-2 bg-error-500/20 text-error-400 rounded-lg hover:bg-error-500/30 transition-all focus-visible:ring-2 focus-visible:ring-error-500/50 disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4 inline mr-2" />
                Clear History
              </Button>
            </div>

            {filteredCommands.length === 0 ? (
              <div className="text-center py-16">
                <History className="w-16 h-16 text-khiimori-text-muted mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-khiimori-text-primary mb-2">No commands found</h3>
                <p className="text-khiimori-text-muted">Start using voice commands to build your history</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredCommands.map((cmd, index) => (
                  <motion.div
                    key={cmd.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="bg-khiimori-bg-elevated/5 backdrop-blur-sm border border-khiimori-border/10 rounded-xl p-4 hover:border-primary-500/50 transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${cmd.executionResult?.success ? 'bg-success-500/20' : 'bg-error-500/20'}`}>
                          {cmd.executionResult?.success ? (
                            <Check className="w-5 h-5 text-success-400" />
                          ) : (
                            <X className="w-5 h-5 text-error-400" />
                          )}
                        </div>
                        <div>
                          <p className="text-khiimori-text-primary font-medium">"{cmd.transcript}"</p>
                          <p className="text-khiimori-text-muted text-sm">
                            {getActionLabel(cmd.action)}
                            {cmd.target && ` - ${cmd.target}`}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-primary-400 text-sm font-medium">
                          {Math.round(cmd.confidence * 100)}%
                        </div>
                        <div className="text-khiimori-text-muted text-xs">
                          {new Date(cmd.timestamp).toLocaleString()}
                        </div>
                        {cmd.duration && (
                          <div className="text-khiimori-text-muted text-xs">
                            {cmd.duration}ms
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'templates' && (
          <div className="space-y-6">
            {/* Header and Add button */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-khiimori-text-primary">Command Templates</h2>
                <p className="text-sm text-khiimori-text-muted mt-0.5">
                  Hands-free voice triggers across browser tabs, navigation, web destinations, and AI.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => { resetTemplateForm(); setShowCreateTemplateModal(true); }}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-all shadow-sm focus-visible:ring-2 focus-visible:ring-primary-500/50"
                >
                  <Plus className="w-4 h-4" />
                  Add Template
                </Button>
              </div>
            </div>

            {/* Filter Pills */}
            <div className="flex items-center gap-2 pb-1 border-b border-khiimori-border/10">
              <button
                onClick={() => setTemplateFilter('all')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  templateFilter === 'all'
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'bg-khiimori-bg-elevated/10 text-khiimori-text-muted hover:text-khiimori-text-primary'
                }`}
              >
                All Templates ({allTemplates.length})
              </button>
              <button
                onClick={() => setTemplateFilter('builtin')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  templateFilter === 'builtin'
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'bg-khiimori-bg-elevated/10 text-khiimori-text-muted hover:text-khiimori-text-primary'
                }`}
              >
                Built-in ({templates.length})
              </button>
              <button
                onClick={() => setTemplateFilter('custom')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  templateFilter === 'custom'
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'bg-khiimori-bg-elevated/10 text-khiimori-text-muted hover:text-khiimori-text-primary'
                }`}
              >
                Custom ({customTemplates.length})
              </button>
            </div>

            {/* Presets Gallery / Quick Add */}
            <div className="p-4 rounded-xl bg-khiimori-bg-elevated/5 border border-khiimori-border/15 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-400" />
                  <span className="text-sm font-semibold text-khiimori-text-primary">
                    Popular Preset Library
                  </span>
                  <span className="text-xs text-khiimori-text-muted">
                    — Click to add custom templates instantly
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2">
                {TEMPLATE_PRESETS.map((preset) => {
                  const isAdded = allTemplates.some(
                    (t) => t.actionType.toLowerCase() === preset.actionType.toLowerCase(),
                  );
                  return (
                    <div
                      key={preset.id}
                      className="p-2.5 rounded-lg bg-khiimori-bg-elevated/10 border border-khiimori-border/10 flex flex-col justify-between hover:border-primary-500/30 transition-all text-left"
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="p-1 rounded bg-khiimori-bg-elevated/20 shrink-0">
                          {getActionIcon(preset.actionType)}
                        </div>
                        <span className="text-xs font-semibold text-khiimori-text-primary truncate">
                          {preset.name}
                        </span>
                      </div>
                      <p className="text-[11px] text-khiimori-text-muted line-clamp-1 mb-2">
                        {preset.patterns[0]}
                      </p>
                      <button
                        onClick={() => handleAddPreset(preset)}
                        disabled={isAdded}
                        className={`w-full py-1 px-2 rounded text-[11px] font-medium transition-all flex items-center justify-center gap-1 ${
                          isAdded
                            ? 'bg-success-500/15 text-success-400 cursor-default'
                            : 'bg-primary-500/15 hover:bg-primary-500/25 text-primary-300'
                        }`}
                      >
                        {isAdded ? (
                          <>
                            <Check className="w-3 h-3" /> Added
                          </>
                        ) : (
                          <>
                            <Plus className="w-3 h-3" /> Add
                          </>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Template Cards Grid */}
            {filteredTemplates.length === 0 ? (
              <div className="p-12 text-center border border-dashed border-khiimori-border/20 rounded-xl bg-khiimori-bg-elevated/5">
                <Command className="w-8 h-8 text-khiimori-text-muted mx-auto mb-2 opacity-50" />
                <p className="text-khiimori-text-primary font-medium">No templates found</p>
                <p className="text-sm text-khiimori-text-muted mt-1">
                  Try adjusting your search query or add a new custom template.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredTemplates.map((template, index) => {
                  const isBuiltin = builtinTemplateIds.has(template.id);
                  return (
                    <motion.div
                      key={template.id}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.03 }}
                      className={`bg-khiimori-bg-elevated/5 backdrop-blur-sm border rounded-xl p-5 transition-all flex flex-col justify-between ${
                        template.enabled
                          ? 'border-khiimori-border/15 hover:border-primary-500/30'
                          : 'border-khiimori-border/10 opacity-60'
                      }`}
                    >
                      <div>
                        {/* Header */}
                        <div className="flex items-start justify-between gap-3 mb-2.5">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`p-2.5 rounded-lg shrink-0 ${template.enabled ? 'bg-primary-500/20' : 'bg-khiimori-bg-elevated/5'}`}>
                              {getActionIcon(template.actionType)}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="text-khiimori-text-primary font-semibold capitalize truncate">
                                  {template.actionType.replace(/_/g, ' ')}
                                </h3>
                                <span
                                  className={`px-1.5 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider ${
                                    isBuiltin
                                      ? 'bg-slate-500/20 text-slate-300'
                                      : 'bg-primary-500/20 text-primary-300'
                                  }`}
                                >
                                  {isBuiltin ? 'Built-in' : 'Custom'}
                                </span>
                              </div>
                              <p className="text-khiimori-text-muted text-xs line-clamp-2 mt-0.5">
                                {template.description}
                              </p>
                            </div>
                          </div>
                          <Button
                            onClick={() => toggleTemplateEnabled(template.id)}
                            className={`px-2.5 py-1 rounded text-xs shrink-0 font-medium transition-all ${
                              template.enabled
                                ? 'bg-success-500/20 text-success-400 hover:bg-success-500/30'
                                : 'bg-khiimori-bg-elevated/10 text-khiimori-text-muted hover:bg-khiimori-bg-elevated/20'
                            }`}
                          >
                            {template.enabled ? 'Enabled' : 'Disabled'}
                          </Button>
                        </div>

                        {/* Destination badges if configured */}
                        {(template.targetUrl || template.targetRoute) && (
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            {template.targetUrl && (
                              <span className="flex items-center gap-1 px-2 py-0.5 bg-sky-500/10 text-sky-300 rounded text-xs truncate max-w-full">
                                <Globe className="w-3 h-3 shrink-0" />
                                <span className="truncate">{template.targetUrl}</span>
                              </span>
                            )}
                            {template.targetRoute && (
                              <span className="flex items-center gap-1 px-2 py-0.5 bg-indigo-500/10 text-indigo-300 rounded text-xs">
                                <Compass className="w-3 h-3 shrink-0" />
                                <span>{template.targetRoute}</span>
                              </span>
                            )}
                          </div>
                        )}

                        {/* Patterns */}
                        <div className="flex flex-wrap gap-1 mb-3">
                          {(template.patterns ?? []).slice(0, 4).map((pattern, i) => (
                            <span
                              key={i}
                              className="px-2 py-0.5 bg-khiimori-bg-elevated/10 text-khiimori-text-secondary rounded text-xs font-mono"
                            >
                              "{pattern}"
                            </span>
                          ))}
                          {(template.patterns ?? []).length > 4 && (
                            <span className="px-2 py-0.5 bg-khiimori-bg-elevated/10 text-khiimori-text-muted rounded text-xs font-mono">
                              +{(template.patterns ?? []).length - 4}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="flex items-center justify-between text-xs text-khiimori-text-muted pt-3 border-t border-khiimori-border/10">
                        <span>{template.usageCount} uses</span>
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={() => handleTestTemplate(template)}
                            className="flex items-center gap-1 px-2.5 py-1 bg-primary-500/15 hover:bg-primary-500/30 text-primary-300 rounded-md transition-all text-xs font-medium"
                            title="Test this template now"
                          >
                            <Play className="w-3 h-3 fill-current" />
                            Run
                          </Button>
                          <Button
                            onClick={() => openEditTemplateModal(template)}
                            aria-label="Edit template"
                            className="p-1.5 hover:bg-khiimori-bg-elevated/20 rounded-md transition-all text-khiimori-text-muted hover:text-khiimori-text-primary"
                            title={isBuiltin ? 'Customize built-in template' : 'Edit template'}
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                          {isBuiltin ? (
                            <span
                              className="p-1.5 opacity-20 cursor-not-allowed text-khiimori-text-muted"
                              title="Built-in templates cannot be deleted"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </span>
                          ) : (
                            <Button
                              onClick={() => {
                                setShowDeleteConfirm(template.id);
                                setSelectedTemplate(template);
                              }}
                              aria-label="Delete template"
                              className="p-1.5 hover:bg-error-500/20 rounded-md transition-all text-error-400"
                              title="Delete template"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Template Modal */}
      <AnimatePresence>
        {showCreateTemplateModal && (
          <Modal
            isOpen={showCreateTemplateModal}
            onClose={() => setShowCreateTemplateModal(false)}
            title="Create Custom Command Template"
            size="md"
          >
            <div className="space-y-4">
              {/* Category selector */}
              <div>
                <label className="block text-khiimori-text-secondary text-xs font-semibold mb-2 uppercase tracking-wider">
                  Template Category
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { key: 'website', label: 'Website / URL', icon: Globe },
                    { key: 'search', label: 'Web Search', icon: Search },
                    { key: 'nav', label: 'Navigation', icon: Compass },
                    { key: 'tabs', label: 'Tab Control', icon: Command },
                    { key: 'ai', label: 'AI Studio', icon: Sparkles },
                    { key: 'custom', label: 'Custom', icon: Tag },
                  ].map((cat) => {
                    const IconComp = cat.icon;
                    return (
                      <button
                        key={cat.key}
                        type="button"
                        onClick={() => setTemplateCategory(cat.key as any)}
                        className={`flex items-center gap-1.5 p-2 rounded-lg text-xs font-medium border transition-all ${
                          templateCategory === cat.key
                            ? 'bg-primary-500/20 border-primary-500/50 text-primary-300'
                            : 'bg-khiimori-bg-elevated/5 border-khiimori-border/10 text-khiimori-text-muted hover:text-khiimori-text-primary'
                        }`}
                      >
                        <IconComp className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">{cat.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Quick Preset Suggestions */}
              {templateCategory === 'website' && (
                <div className="p-2.5 rounded-lg bg-khiimori-bg-elevated/5 border border-khiimori-border/10">
                  <span className="text-[11px] text-khiimori-text-muted block mb-1.5">Quick fill site:</span>
                  <div className="flex gap-1.5 flex-wrap">
                    {[
                      { name: 'YouTube', type: 'open_youtube', url: 'https://www.youtube.com', desc: 'Open YouTube video player', pattern: 'open youtube' },
                      { name: 'GitHub', type: 'open_github', url: 'https://github.com', desc: 'Open GitHub dashboard', pattern: 'open github' },
                      { name: 'Gmail', type: 'open_gmail', url: 'https://mail.google.com', desc: 'Open Google Mail inbox', pattern: 'open mail' },
                      { name: 'Notion', type: 'open_notion', url: 'https://www.notion.so', desc: 'Open Notion workspace', pattern: 'open notion' },
                      { name: 'Spotify', type: 'open_spotify', url: 'https://open.spotify.com', desc: 'Open Spotify web player', pattern: 'open spotify' },
                    ].map((s) => (
                      <button
                        key={s.name}
                        type="button"
                        onClick={() => {
                          setTemplateFormData({
                            actionType: s.type,
                            description: s.desc,
                            targetUrl: s.url,
                            targetRoute: '',
                            patterns: [s.pattern, `go to ${s.name.toLowerCase()}`],
                          });
                        }}
                        className="px-2 py-1 rounded bg-khiimori-bg-elevated/10 hover:bg-primary-500/20 text-xs text-khiimori-text-secondary hover:text-primary-300 transition-all"
                      >
                        {s.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {templateCategory === 'search' && (
                <div className="p-2.5 rounded-lg bg-khiimori-bg-elevated/5 border border-khiimori-border/10">
                  <span className="text-[11px] text-khiimori-text-muted block mb-1.5">Quick fill search engine:</span>
                  <div className="flex gap-1.5 flex-wrap">
                    {[
                      { name: 'Google', type: 'search_google', url: 'https://www.google.com/search?q={query}', desc: 'Search Google with voice query', pattern: 'google {query}' },
                      { name: 'YouTube', type: 'search_youtube', url: 'https://www.youtube.com/results?search_query={query}', desc: 'Search YouTube videos', pattern: 'search youtube for {query}' },
                      { name: 'Reddit', type: 'search_reddit', url: 'https://www.reddit.com/search/?q={query}', desc: 'Search Reddit discussions', pattern: 'search reddit for {query}' },
                      { name: 'Wikipedia', type: 'search_wikipedia', url: 'https://en.wikipedia.org/wiki/Special:Search?search={query}', desc: 'Search Wikipedia articles', pattern: 'search wikipedia for {query}' },
                    ].map((s) => (
                      <button
                        key={s.name}
                        type="button"
                        onClick={() => {
                          setTemplateFormData({
                            actionType: s.type,
                            description: s.desc,
                            targetUrl: s.url,
                            targetRoute: '',
                            patterns: [s.pattern, `${s.name.toLowerCase()} {query}`],
                          });
                        }}
                        className="px-2 py-1 rounded bg-khiimori-bg-elevated/10 hover:bg-primary-500/20 text-xs text-khiimori-text-secondary hover:text-primary-300 transition-all"
                      >
                        {s.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {templateCategory === 'nav' && (
                <div className="p-2.5 rounded-lg bg-khiimori-bg-elevated/5 border border-khiimori-border/10">
                  <span className="text-[11px] text-khiimori-text-muted block mb-1.5">Quick fill navigation route:</span>
                  <div className="flex gap-1.5 flex-wrap">
                    {[
                      { name: 'Productivity', type: 'nav_productivity', route: '/productivity', desc: 'Go to Productivity Center', pattern: 'open productivity' },
                      { name: 'AI Studio', type: 'nav_ai', route: '/ai', desc: 'Go to Conversational AI', pattern: 'open ai studio' },
                      { name: 'Insights', type: 'nav_insights', route: '/insights', desc: 'Go to Insights dashboard', pattern: 'open insights' },
                      { name: 'Home', type: 'nav_home', route: '/home', desc: 'Return to Home overview', pattern: 'go home' },
                      { name: 'Settings', type: 'nav_settings', route: '/settings', desc: 'Open Settings panel', pattern: 'open settings' },
                    ].map((s) => (
                      <button
                        key={s.name}
                        type="button"
                        onClick={() => {
                          setTemplateFormData({
                            actionType: s.type,
                            description: s.desc,
                            targetUrl: '',
                            targetRoute: s.route,
                            patterns: [s.pattern, `go to ${s.name.toLowerCase()}`],
                          });
                        }}
                        className="px-2 py-1 rounded bg-khiimori-bg-elevated/10 hover:bg-primary-500/20 text-xs text-khiimori-text-secondary hover:text-primary-300 transition-all"
                      >
                        {s.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-khiimori-text-secondary text-sm mb-1">Action Type / Identifier *</label>
                <Input
                  type="text"
                  value={templateFormData.actionType}
                  onChange={(e) => setTemplateFormData(prev => ({ ...prev, actionType: e.target.value }))}
                  placeholder="e.g., open_youtube, search_recipes, nav_focus"
                  className="w-full px-4 py-2 bg-khiimori-bg-elevated/5 border border-khiimori-border/10 rounded-lg text-khiimori-text-primary placeholder-khiimori-text-muted focus:outline-none focus:border-primary-500/50"
                />
              </div>

              <div>
                <label className="block text-khiimori-text-secondary text-sm mb-1">Description</label>
                <Input
                  type="text"
                  value={templateFormData.description}
                  onChange={(e) => setTemplateFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="What this command does"
                  className="w-full px-4 py-2 bg-khiimori-bg-elevated/5 border border-khiimori-border/10 rounded-lg text-khiimori-text-primary placeholder-khiimori-text-muted focus:outline-none focus:border-primary-500/50"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-khiimori-text-secondary text-sm mb-1">
                    Target URL <span className="text-khiimori-text-muted text-xs">(optional)</span>
                  </label>
                  <Input
                    type="text"
                    value={templateFormData.targetUrl}
                    onChange={(e) => setTemplateFormData(prev => ({ ...prev, targetUrl: e.target.value }))}
                    placeholder="https://... (use {query} for dynamic search)"
                    className="w-full px-3 py-2 bg-khiimori-bg-elevated/5 border border-khiimori-border/10 rounded-lg text-khiimori-text-primary text-xs placeholder-khiimori-text-muted focus:outline-none focus:border-primary-500/50"
                  />
                </div>
                <div>
                  <label className="block text-khiimori-text-secondary text-sm mb-1">
                    Target Route <span className="text-khiimori-text-muted text-xs">(optional)</span>
                  </label>
                  <Input
                    type="text"
                    value={templateFormData.targetRoute}
                    onChange={(e) => setTemplateFormData(prev => ({ ...prev, targetRoute: e.target.value }))}
                    placeholder="/productivity, /ai, /settings"
                    className="w-full px-3 py-2 bg-khiimori-bg-elevated/5 border border-khiimori-border/10 rounded-lg text-khiimori-text-primary text-xs placeholder-khiimori-text-muted focus:outline-none focus:border-primary-500/50"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-khiimori-text-secondary text-sm">Voice Patterns</label>
                  <div className="flex items-center gap-1.5 text-xs text-khiimori-text-muted">
                    <span>Insert:</span>
                    <button
                      type="button"
                      onClick={() => setPatternInput((prev) => `${prev} {query}`.trim())}
                      className="px-1.5 py-0.5 rounded bg-primary-500/10 hover:bg-primary-500/20 text-primary-300 font-mono text-[11px]"
                    >
                      + {"{query}"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPatternInput((prev) => `${prev} {tab}`.trim())}
                      className="px-1.5 py-0.5 rounded bg-primary-500/10 hover:bg-primary-500/20 text-primary-300 font-mono text-[11px]"
                    >
                      + {"{tab}"}
                    </button>
                  </div>
                </div>

                <div className="flex gap-1.5 mb-2 flex-wrap">
                  {templateFormData.patterns.map((pattern) => (
                    <span
                      key={pattern}
                      className="flex items-center gap-1 px-2.5 py-1 bg-primary-500/15 text-primary-300 rounded-lg text-xs font-mono"
                    >
                      "{pattern}"
                      <button
                        type="button"
                        onClick={() => handleRemovePattern(pattern)}
                        aria-label={`Remove pattern ${pattern}`}
                        className="hover:text-white"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>

                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={patternInput}
                    onChange={(e) => setPatternInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddPattern())}
                    placeholder="e.g., open youtube, search google for {query}"
                    className="flex-1 px-4 py-2 bg-khiimori-bg-elevated/5 border border-khiimori-border/10 rounded-lg text-khiimori-text-primary placeholder-khiimori-text-muted focus:outline-none focus:border-primary-500/50"
                  />
                  <Button
                    type="button"
                    onClick={handleAddPattern}
                    className="px-4 py-2 bg-khiimori-bg-elevated/10 hover:bg-khiimori-bg-elevated/20 rounded-lg text-khiimori-text-primary font-medium"
                  >
                    Add Pattern
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                onClick={() => setShowCreateTemplateModal(false)}
                className="flex-1 px-4 py-2 bg-khiimori-bg-elevated/5 text-khiimori-text-secondary rounded-lg hover:bg-khiimori-bg-elevated/10 transition-all font-medium"
              >
                Cancel
              </Button>
              <Button
                onClick={createTemplate}
                disabled={!templateFormData.actionType.trim()}
                className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-all flex items-center justify-center gap-2 disabled:opacity-50 font-medium shadow-sm"
              >
                <Save className="w-4 h-4" />
                Create Template
              </Button>
            </div>
          </Modal>
        )}
      </AnimatePresence>

      {/* Edit Template Modal */}
      <AnimatePresence>
        {showEditTemplateModal && selectedTemplate && (
          <Modal
            isOpen={showEditTemplateModal}
            onClose={() => setShowEditTemplateModal(false)}
            title="Edit Command Template"
            size="md"
          >
            <div className="space-y-4">
              <div>
                <label className="block text-khiimori-text-secondary text-sm mb-1">Action Type *</label>
                <Input
                  type="text"
                  value={templateFormData.actionType}
                  onChange={(e) => setTemplateFormData(prev => ({ ...prev, actionType: e.target.value }))}
                  className="w-full px-4 py-2 bg-khiimori-bg-elevated/5 border border-khiimori-border/10 rounded-lg text-khiimori-text-primary focus:outline-none focus:border-primary-500/50"
                />
              </div>

              <div>
                <label className="block text-khiimori-text-secondary text-sm mb-1">Description</label>
                <Input
                  type="text"
                  value={templateFormData.description}
                  onChange={(e) => setTemplateFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-2 bg-khiimori-bg-elevated/5 border border-khiimori-border/10 rounded-lg text-khiimori-text-primary focus:outline-none focus:border-primary-500/50"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-khiimori-text-secondary text-sm mb-1">Target URL</label>
                  <Input
                    type="text"
                    value={templateFormData.targetUrl}
                    onChange={(e) => setTemplateFormData(prev => ({ ...prev, targetUrl: e.target.value }))}
                    placeholder="https://... ({query})"
                    className="w-full px-3 py-2 bg-khiimori-bg-elevated/5 border border-khiimori-border/10 rounded-lg text-khiimori-text-primary text-xs"
                  />
                </div>
                <div>
                  <label className="block text-khiimori-text-secondary text-sm mb-1">Target Route</label>
                  <Input
                    type="text"
                    value={templateFormData.targetRoute}
                    onChange={(e) => setTemplateFormData(prev => ({ ...prev, targetRoute: e.target.value }))}
                    placeholder="/productivity, /ai"
                    className="w-full px-3 py-2 bg-khiimori-bg-elevated/5 border border-khiimori-border/10 rounded-lg text-khiimori-text-primary text-xs"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-khiimori-text-secondary text-sm">Voice Patterns</label>
                  <div className="flex items-center gap-1.5 text-xs text-khiimori-text-muted">
                    <span>Insert:</span>
                    <button
                      type="button"
                      onClick={() => setPatternInput((prev) => `${prev} {query}`.trim())}
                      className="px-1.5 py-0.5 rounded bg-primary-500/10 hover:bg-primary-500/20 text-primary-300 font-mono text-[11px]"
                    >
                      + {"{query}"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPatternInput((prev) => `${prev} {tab}`.trim())}
                      className="px-1.5 py-0.5 rounded bg-primary-500/10 hover:bg-primary-500/20 text-primary-300 font-mono text-[11px]"
                    >
                      + {"{tab}"}
                    </button>
                  </div>
                </div>

                <div className="flex gap-1.5 mb-2 flex-wrap">
                  {templateFormData.patterns.map((pattern) => (
                    <span
                      key={pattern}
                      className="flex items-center gap-1 px-2.5 py-1 bg-primary-500/15 text-primary-300 rounded-lg text-xs font-mono"
                    >
                      "{pattern}"
                      <button
                        type="button"
                        onClick={() => handleRemovePattern(pattern)}
                        aria-label={`Remove pattern ${pattern}`}
                        className="hover:text-white"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>

                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={patternInput}
                    onChange={(e) => setPatternInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddPattern())}
                    placeholder="Add pattern"
                    className="flex-1 px-4 py-2 bg-khiimori-bg-elevated/5 border border-khiimori-border/10 rounded-lg text-khiimori-text-primary placeholder-khiimori-text-muted focus:outline-none focus:border-primary-500/50"
                  />
                  <Button
                    type="button"
                    onClick={handleAddPattern}
                    className="px-4 py-2 bg-khiimori-bg-elevated/10 hover:bg-khiimori-bg-elevated/20 rounded-lg text-khiimori-text-primary font-medium"
                  >
                    Add
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                onClick={() => setShowEditTemplateModal(false)}
                className="flex-1 px-4 py-2 bg-khiimori-bg-elevated/5 text-khiimori-text-secondary rounded-lg hover:bg-khiimori-bg-elevated/10 transition-all font-medium"
              >
                Cancel
              </Button>
              <Button
                onClick={() => updateTemplate(selectedTemplate.id)}
                className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-all flex items-center justify-center gap-2 font-medium shadow-sm"
              >
                <Save className="w-4 h-4" />
                Save Changes
              </Button>
            </div>
          </Modal>
        )}
      </AnimatePresence>

      {/* Delete Confirmation */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <ConfirmDialog
            isOpen={!!showDeleteConfirm}
            onClose={() => setShowDeleteConfirm(null)}
            title="Delete Template?"
            message="This action cannot be undone. The template will be permanently removed."
            confirmText="Delete"
            cancelText="Cancel"
            onConfirm={() => deleteTemplate(showDeleteConfirm)}
            variant="danger"
            icon={<Trash2 className="w-8 h-8 text-error-400" />}
          />
        )}
      </AnimatePresence>
    </PageLayout>
  );
}